import { Injectable } from '@angular/core';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { KEYS } from '../constants/keys';

@Injectable({
  providedIn: 'root'
})
export class IdleService {
  private idleState = 'Not started.';
  private timedOut = false;
  private lastPing?: Date = null;

  private warningActive = false;
  private countdownSnackBarRef: MatSnackBarRef<any> | null = null;
  private configured = false;

  constructor(
    private idle: Idle,
    private keepalive: Keepalive,
    private router: Router,
    private logger: NGXLogger,
    private snackBar: MatSnackBar
  ) {}

  startWatching(): void {
    this.configure();
    // ng-idle keeps its deadline in localStorage, which every public auth page clears.
    // Only watch once there is actually a session to time out.
    if (this.isAuthenticated()) {
      this.reset();
    } else {
      this.stop();
    }
  }

  reset(): void {
    this.configure();
    if (!this.isAuthenticated()) {
      this.abortWatch();
      return;
    }
    this.idle.watch();
    this.idleState = 'Started.';
    this.timedOut = false;
    this.warningActive = false;
    this.closeCountdownSnackBar();
  }

  /**
   * Wires the idle timings, event handlers and keepalive exactly once, so repeat
   * calls to startWatching()/reset() cannot register duplicate subscriptions.
   */
  private configure(): void {
    if (this.configured) {
      return;
    }
    this.configured = true;

    this.idle.setIdle(1800);
    this.idle.setTimeout(10);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.onIdleEnd.subscribe(() => {
      this.idleState = 'No longer idle.';
      this.closeCountdownSnackBar();
    });

    this.idle.onTimeout.subscribe(() => {
      this.idleState = 'Timed out!';
      this.timedOut = true;
      this.handleTimeout();
    });

    this.idle.onIdleStart.subscribe(() => {
      if (!this.isAuthenticated()) {
        this.abortWatch();
        return;
      }
      this.idleState = "You've gone idle!";
    });

    this.idle.onTimeoutWarning.subscribe((countdown: number) => {
      if (!this.isAuthenticated()) {
        this.abortWatch();
        return;
      }
      this.idleState = `You will time out in ${countdown} seconds!`;

      if (!this.warningActive) {
        this.openCountdownSnackBar(countdown);
        this.warningActive = true;
      } else {
        this.updateCountdownSnackBar(countdown);
      }
    });

    this.keepalive.interval(15);
    this.keepalive.onPing.subscribe(() => this.lastPing = new Date());
  }

  private isAuthenticated(): boolean {
    return !!localStorage.getItem(KEYS.ACCESS_TOKEN);
  }

  /**
   * localStorage was cleared while the watch was running (a public auth page, or a
   * logout in another tab), so ng-idle finds no expiry and reports an instant
   * timeout. There is no session left to expire — stop quietly rather than warn
   * the user and redirect them.
   */
  private abortWatch(): void {
    this.logger.info('Idle watch stopped: no active session.');
    this.idleState = 'Not started.';
    this.timedOut = false;
    this.stop();
  }

  stop(): void {
    this.idle.stop();
    this.closeCountdownSnackBar();
  }

    private openCountdownSnackBar(initialCountdown: number): void {
        this.countdownSnackBarRef = this.snackBar.open(
            `⚠️ You will be logged out in ${initialCountdown} seconds. Move your mouse or press a key to stay logged in.`,
            '',
            {
                duration: undefined,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['timeout-snackbar']
            }
        );
    }

  private updateCountdownSnackBar(countdown: number): void {
    if (this.countdownSnackBarRef) {
      this.countdownSnackBarRef.dismiss();
      this.openCountdownSnackBar(countdown);
    }
  }

  private closeCountdownSnackBar(): void {
    if (this.countdownSnackBarRef) {
      this.countdownSnackBarRef.dismiss();
      this.countdownSnackBarRef = null;
      this.warningActive = false;
    }
  }

    private handleTimeout(): void {
        if (!this.isAuthenticated()) {
            this.abortWatch();
            return;
        }
        localStorage.clear();
        sessionStorage.clear();
        sessionStorage.setItem('sessionExpired', 'true');
        window.location.href = '/login';
    }

}
